import { useMemo } from 'react';
import { useInstallability } from '../../hooks/useInstallability';
export function useAppUninstallableReason({
  appContext,
  listingId,
  features: __features,
  published,
  isMcpServer,
  hasMcpServerAccess,
  skip
}) {
  const {
    data: installabilityData,
    loading: installabilityLoading,
    error: installabilityError,
    refetch: refetchInstallability
  } = useInstallability({
    listingId,
    skip: skip || !listingId,
    published,
    appContext
  });
  const uninstallableReason = useMemo(() => {
    var _installabilityData$i, _installabilityData$i2, _installabilityData$i3, _installabilityData$d, _uninstallableReasonD, _uninstallableReasonD2, _productSpecific$miss, _productSpecific$miss2, _productSpecific$miss3, _productSpecific$miss4, _productSpecific$miss5, _productSpecific$miss6, _ref, _productSpecific$inco;
    if (!installabilityData) return undefined;
    if (installabilityData.installed) {
      return {
        label: 'ALREADY_INSTALLED'
      };
    }

    // Check MCP server access - portal doesn't have access to this MCP server
    if (isMcpServer && !hasMcpServerAccess) {
      return {
        label: 'MCP_SERVER_UNAVAILABLE'
      };
    }
    const uninstallableReasonData = (_installabilityData$i = installabilityData.installability) === null || _installabilityData$i === void 0 ? void 0 : _installabilityData$i.uninstallableReason;
    const portalInstallable = (_installabilityData$i2 = installabilityData.installability) === null || _installabilityData$i2 === void 0 ? void 0 : _installabilityData$i2.portalInstallable;
    const userInstallable = (_installabilityData$i3 = installabilityData.installability) === null || _installabilityData$i3 === void 0 ? void 0 : _installabilityData$i3.userInstallable;
    const missingUserRoles = // Field expected at the same level as installability in the return payload
    // Fallback to empty array if not present in the current schema
    (_installabilityData$d = installabilityData === null || installabilityData === void 0 ? void 0 : installabilityData.derivedMissingUserRoles) !== null && _installabilityData$d !== void 0 ? _installabilityData$d : [];
    const productSpecific = uninstallableReasonData === null || uninstallableReasonData === void 0 ? void 0 : uninstallableReasonData.productSpecificUninstallableReason;

    // Portal must enable sensitive data before this app can be installed
    // @ts-expect-error field pending backend schema update — SIERP-178
    if (productSpecific !== null && productSpecific !== void 0 && productSpecific.sensitiveDataRequired) {
      return {
        label: 'SENSITIVE_DATA_REQUIRED'
      };
    }
    const missingPortalScopes = (_uninstallableReasonD = uninstallableReasonData === null || uninstallableReasonData === void 0 ? void 0 : uninstallableReasonData.missingPortalScopes) !== null && _uninstallableReasonD !== void 0 ? _uninstallableReasonD : [];
    const missingUserScopes = (_uninstallableReasonD2 = uninstallableReasonData === null || uninstallableReasonData === void 0 ? void 0 : uninstallableReasonData.missingUserScopes) !== null && _uninstallableReasonD2 !== void 0 ? _uninstallableReasonD2 : [];
    const productSpecificMissingPortalScopes = (_productSpecific$miss = productSpecific === null || productSpecific === void 0 || (_productSpecific$miss2 = productSpecific.missingPortalInternalScopes) === null || _productSpecific$miss2 === void 0 ? void 0 : _productSpecific$miss2.flat()) !== null && _productSpecific$miss !== void 0 ? _productSpecific$miss : [];
    const productSpecificMissingUserScopes = (_productSpecific$miss3 = productSpecific === null || productSpecific === void 0 || (_productSpecific$miss4 = productSpecific.missingUserInternalScopes) === null || _productSpecific$miss4 === void 0 ? void 0 : _productSpecific$miss4.flat()) !== null && _productSpecific$miss3 !== void 0 ? _productSpecific$miss3 : [];
    const missingPortalScopeGroups = (_productSpecific$miss5 = productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific.missingPortalScopeGroups) !== null && _productSpecific$miss5 !== void 0 ? _productSpecific$miss5 : [];
    const missingUserScopeGroups = (_productSpecific$miss6 = productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific.missingUserScopeGroups) !== null && _productSpecific$miss6 !== void 0 ? _productSpecific$miss6 : [];

    // MISSING_HUB_SCOPES if the app is not installable at the portal level based on scopes
    if (missingPortalScopes.length || missingPortalScopeGroups.length || productSpecificMissingPortalScopes.length) {
      return {
        label: 'MISSING_HUB_SCOPES',
        missingScopes: [...missingPortalScopes, ...missingPortalScopeGroups, ...productSpecificMissingPortalScopes]
      };
    }

    // MISSING_USER_PERMISSIONS if the app is installable at the portal level but not at the user level based on scopes
    if (portalInstallable && (missingUserScopes.length || missingUserScopeGroups.length || productSpecificMissingUserScopes.length)) {
      return {
        label: 'MISSING_USER_PERMISSIONS',
        missingUserRoles
      };
    }

    /**
     * We are leaving the below commented out for now because the BE
     * is planning to map old integrationFeatures onto the UDS V3 features
     * with missing scope group data. In that case, the featureCompatibilities
     * info becomes irrelevant/misleading, so until we've accounted for that
     * more robustly, we're going to skip this NO_COMPATIBLE_FEATURES check
     */

    // NO_COMPATIBLE_FEATURES: if every feature has no overlap between
    // portal tiers and the feature's compatible tiers

    // const portalTiers = installCompatibility.portalTiers || [];
    // const featureCompatibilities: FeatureCompatibility[] =
    //   installCompatibility.featureCompatibilities || [];

    // if (portalTiers.length && featureCompatibilities.length) {
    //   const allFeaturesIncompatible = (
    //     Array.isArray(features) ? features : []
    //   )
    //     .map((currentFeature) => {
    //       const compat = featureCompatibilities.find(
    //         (fc) => fc.featureName === currentFeature.featureName
    //       );
    //       if (!compat) return false; // if unknown, assume compatible
    //       const hasOverlap = compat.compatibleTiers.some((tier: string) =>
    //         (portalTiers as string[]).includes(tier)
    //       );
    //       return !hasOverlap;
    //     })
    //     .every((value) => value === true);

    //   if (
    //     Array.isArray(features) &&
    //     features.length &&
    //     allFeaturesIncompatible
    //   ) {
    //     return { label: 'NO_COMPATIBLE_FEATURES' };
    //   }
    // }

    const incompatibleCombined = (_ref = ((_productSpecific$inco = productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific.incompatiblePortalInstalledAppIds) !== null && _productSpecific$inco !== void 0 ? _productSpecific$inco : []).length ? productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific.incompatiblePortalInstalledAppIds : productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific.incompatibleUserInstalledAppIds) !== null && _ref !== void 0 ? _ref : [];
    if (incompatibleCombined.length) {
      return {
        label: 'INCOMPATIBLE_INSTALLS',
        incompatibleInstalls: incompatibleCombined
      };
    }
    if (!portalInstallable && !userInstallable) return {
      label: 'UNKNOWN_UNINSTALLABLE_REASON'
    };
  }, [installabilityData, isMcpServer, hasMcpServerAccess]);
  const missingScopes = useMemo(() => {
    var _installabilityData$i4, _installabilityData$i5, _installabilityData$i6, _installabilityData$i7, _installabilityData$i8, _productSpecific$miss7, _productSpecific$miss8, _productSpecific$miss9, _productSpecific$miss10;
    const productSpecific = installabilityData === null || installabilityData === void 0 || (_installabilityData$i4 = installabilityData.installability) === null || _installabilityData$i4 === void 0 || (_installabilityData$i4 = _installabilityData$i4.uninstallableReason) === null || _installabilityData$i4 === void 0 ? void 0 : _installabilityData$i4.productSpecificUninstallableReason;
    const missingUserScopes = (_installabilityData$i5 = installabilityData === null || installabilityData === void 0 || (_installabilityData$i6 = installabilityData.installability) === null || _installabilityData$i6 === void 0 || (_installabilityData$i6 = _installabilityData$i6.uninstallableReason) === null || _installabilityData$i6 === void 0 ? void 0 : _installabilityData$i6.missingUserScopes) !== null && _installabilityData$i5 !== void 0 ? _installabilityData$i5 : [];
    const missingPortalScopes = (_installabilityData$i7 = installabilityData === null || installabilityData === void 0 || (_installabilityData$i8 = installabilityData.installability) === null || _installabilityData$i8 === void 0 || (_installabilityData$i8 = _installabilityData$i8.uninstallableReason) === null || _installabilityData$i8 === void 0 ? void 0 : _installabilityData$i8.missingPortalScopes) !== null && _installabilityData$i7 !== void 0 ? _installabilityData$i7 : [];
    const productSpecificMissingPortalScopes = (_productSpecific$miss7 = productSpecific === null || productSpecific === void 0 || (_productSpecific$miss8 = productSpecific.missingPortalInternalScopes) === null || _productSpecific$miss8 === void 0 ? void 0 : _productSpecific$miss8.flat()) !== null && _productSpecific$miss7 !== void 0 ? _productSpecific$miss7 : [];
    const productSpecificMissingUserScopes = (_productSpecific$miss9 = productSpecific === null || productSpecific === void 0 || (_productSpecific$miss10 = productSpecific.missingUserInternalScopes) === null || _productSpecific$miss10 === void 0 ? void 0 : _productSpecific$miss10.flat()) !== null && _productSpecific$miss9 !== void 0 ? _productSpecific$miss9 : [];
    const allMissingScopes = [...missingUserScopes.map(scope => scope.toLowerCase().replaceAll('_', '-')), ...missingPortalScopes.map(scope => scope.toLowerCase().replaceAll('_', '-')), ...productSpecificMissingPortalScopes, ...productSpecificMissingUserScopes];

    // Dedupe missing scopes
    return [...new Set(allMissingScopes)];
  }, [installabilityData]);
  const missingScopeGroups = useMemo(() => {
    var _installabilityData$i9, _productSpecific$miss11, _productSpecific$miss12, _productSpecific$miss13, _productSpecific$miss14;
    const productSpecific = installabilityData === null || installabilityData === void 0 || (_installabilityData$i9 = installabilityData.installability) === null || _installabilityData$i9 === void 0 || (_installabilityData$i9 = _installabilityData$i9.uninstallableReason) === null || _installabilityData$i9 === void 0 ? void 0 : _installabilityData$i9.productSpecificUninstallableReason;
    const productSpecificMissingPortalScopeGroups = (_productSpecific$miss11 = productSpecific === null || productSpecific === void 0 || (_productSpecific$miss12 = productSpecific.missingPortalScopeGroups) === null || _productSpecific$miss12 === void 0 ? void 0 : _productSpecific$miss12.flat()) !== null && _productSpecific$miss11 !== void 0 ? _productSpecific$miss11 : [];
    const productSpecificMissingUserScopeGroups = (_productSpecific$miss13 = productSpecific === null || productSpecific === void 0 || (_productSpecific$miss14 = productSpecific.missingUserScopeGroups) === null || _productSpecific$miss14 === void 0 ? void 0 : _productSpecific$miss14.flat()) !== null && _productSpecific$miss13 !== void 0 ? _productSpecific$miss13 : [];
    const allMissingScopeGroups = [...productSpecificMissingPortalScopeGroups, ...productSpecificMissingUserScopeGroups];

    // Dedupe missing scope groups
    return [...new Set(allMissingScopeGroups)];
  }, [installabilityData]);
  return {
    uninstallableReason,
    missingScopeGroups,
    missingScopes,
    loading: installabilityLoading,
    error: installabilityError,
    refetch: refetchInstallability
  };
}